const S3_HOST_PATTERN = /\.s3[.-][a-z0-9-]+\.amazonaws\.com/i;

const PRESIGNED_URL_PATTERN = /[?&]X-Amz-Signature=/i;

export const DEFAULT_AVATAR = "/default_avatar.png";

function isPresignedUrl(url: string): boolean {
	return PRESIGNED_URL_PATTERN.test(url);
}

function getCdnBaseUrl(): string | undefined {
	const cdnBase = import.meta.env.VITE_IMAGE_CDN_URL;
	return cdnBase && cdnBase.length > 0 ? cdnBase.replace(/\/$/, "") : undefined;
}

function rewriteToCdnUrl(url: string): string {
	const cdnBase = getCdnBaseUrl();
	if (!cdnBase) return url;

	try {
		const parsed = new URL(url);
		if (!S3_HOST_PATTERN.test(parsed.hostname)) return url;
		return `${cdnBase}${parsed.pathname}`;
	} catch {
		return url;
	}
}

/** Whether a URL can receive CDN resize hints (`?w=N`). */
export function isResizableImageUrl(url: string | undefined | null): boolean {
	if (!url) return false;
	if (url.startsWith("/") || url.startsWith("data:")) return false;
	if (isPresignedUrl(url)) return false;

	if (S3_HOST_PATTERN.test(url)) return true;

	const cdnBase = getCdnBaseUrl();
	return Boolean(cdnBase && url.startsWith(cdnBase));
}

export function getResponsiveImageUrl(url: string, widthPx: number): string {
	if (!isResizableImageUrl(url)) return url;

	const baseUrl = rewriteToCdnUrl(url);
	const separator = baseUrl.includes("?") ? "&" : "?";
	return `${baseUrl}${separator}w=${widthPx}`;
}

export interface ResponsiveImageProps {
	src: string;
	srcSet?: string;
	width: number;
	height: number;
}

/** Build src/srcSet/width/height for a thumbnail displayed at `displaySizePx`. */
export function getResponsiveImageProps(
	url: string | undefined | null,
	displaySizePx: number,
	fallback = DEFAULT_AVATAR,
): ResponsiveImageProps {
	const base = url || fallback;

	if (!isResizableImageUrl(base)) {
		return {
			src: base,
			width: displaySizePx,
			height: displaySizePx,
		};
	}

	const src1x = getResponsiveImageUrl(base, displaySizePx * 2);
	const src2x = getResponsiveImageUrl(base, displaySizePx * 4);

	return {
		src: src1x,
		srcSet: `${src1x} 1x, ${src2x} 2x`,
		width: displaySizePx,
		height: displaySizePx,
	};
}
